import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import PrintStock from "./PrintStock";
import { useAppSelector } from "hooks/hooks";
import { selectState, setSearchInputPopover } from "../inputSlice";
import { toast } from "react-toastify";
import { useAppDispatch } from "hooks/hooks";
import ToolboxButton, {
  ToolboxButtonIcon,
} from "Dashboard/components/Buttons/ToolboxButton";
import { useNavigate } from "react-router-dom";
import Popover from "components/Popover/Popover";
import NewStockInputPopover from "./NewStockInputPopover";
import NewStockOutputToProjectOrPartnerPopover from "../../Output/components/NewStockOutputToProjectOrPartnerPopover";
import SearchPopover from "./SearchPopover";
import InfoTooltip from "components/Popup/InfoTooltip";
import {
  Control,
  useForm,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus,
  UseFormSetValue
} from "react-hook-form";
import { IStockInputCreate } from "Dashboard/scenes/Stock/domain/IStockInputCreate";
import { IOption } from "Dashboard/domain/IOption";
import { SearchType } from "Dashboard/domain/SearchType";
import { IGetStockInputsRequestParams } from "Dashboard/scenes/Stock/domain/IGetStockInputsRequestParams";
import { IStockOutputForProjectOrPartnerCreate } from "Dashboard/scenes/Stock/domain/IStockOutputForProjectOrPartnerCreate";
import NewStockOutputToEmployeePopover from "../../Output/components/NewStockOutputToEmployeePopover";
import { IStockOutputForEmployeeCreate } from "Dashboard/scenes/Stock/domain/IStockOutputForEmployeeCreate";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasWriteAccessTo } from "utils/authUtil";
import { useSelector } from "react-redux";
import { useLocalStorage } from "hooks/LocalStorage";
import { usePagePrint } from "hooks/usePagePrint";
import { STOCK_MODULE_NAME } from "Dashboard/scenes/Stock/domain/StockModule";
import { areQueryParamsEmpty } from "utils/queryParamsUtil";

interface IProps {
  defaultGetInputsRequestParams: IGetStockInputsRequestParams;
  setGetInputsRequestParams: Dispatch<SetStateAction<IGetStockInputsRequestParams>>;
  registerSearch: UseFormRegister<IGetStockInputsRequestParams>;
  handleSubmitSearch: UseFormHandleSubmit<IGetStockInputsRequestParams>;
  resetSearch: UseFormReset<IGetStockInputsRequestParams>;
  controlSearch: Control<IGetStockInputsRequestParams, any>;
  setFocusSearch: UseFormSetFocus<IGetStockInputsRequestParams>;
  getValuesSearch: UseFormGetValues<IGetStockInputsRequestParams>;
  setValueSearch: UseFormSetValue<IGetStockInputsRequestParams>;
};

const Toolbox = ({
  defaultGetInputsRequestParams,
  setGetInputsRequestParams,
  registerSearch,
  handleSubmitSearch,
  resetSearch,
  controlSearch,
  setFocusSearch,
  getValuesSearch,
  setValueSearch,
}: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = usePagePrint(componentRef, { margins: "0.5cm" });

  const { outputValues, searchInputPopover } = useAppSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const [isOpenNewInput, setIsOpenNewInput] = useState(false);
  const [isOpenOutputForProjectOrPartner, setIsOpenOutputForProjectOrPartner] = useState(false);
  const [isOpenOutputForEmployee, setIsOpenOutputForEmployee] = useState(false);

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<IStockInputCreate>();
  const [knownItemCodes, setKnownItemCodes] = useState<string[]>([]);
  const [supplierValue, setSupplierValue] = useState<IOption | undefined>(undefined);
  const [driverValue, setDriverValue] = useState<IOption | undefined>(undefined);

  const [searchTypeValue, setSearchTypeValue] = useState(SearchType.BASIC);
  const [storedSearchTerm, setStoredSearchTerm]
    = useLocalStorage("stockSearchTerm", "");
  const [storedSearchType, setStoredSearchType]
    = useLocalStorage("stockSearchType", SearchType.BASIC);
  const [storedSearchInputs, setStoredSearchInputs]
    = useLocalStorage("stockSearchInputs", null);

  const isSearchFormEmpty = areQueryParamsEmpty(getValuesSearch(), ["stock"], true);

  useEffect(() => {
    if (storedSearchType === "BASIC") {
      setGetInputsRequestParams({
        stock: "Lager", searchTerm: storedSearchTerm
      });
    }
    if (storedSearchType === "ADVANCED") {
      storedSearchInputs.searchTerm = undefined;
      setGetInputsRequestParams({
        stock: "Lager", ...storedSearchInputs
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  useEffect(() => {
    if (storedSearchTerm) {
      setValueSearch("searchTerm", storedSearchTerm);
    }
    if (storedSearchType) {
      setSearchTypeValue(storedSearchType);
    }
    if (storedSearchInputs) {
      Object.keys(storedSearchInputs).forEach((key: any) => {
        if (key !== "searchTerm") {
          setValueSearch(key, storedSearchInputs[key]);
        }
      });
    }
  }, [
    setValueSearch,
    storedSearchTerm,
    storedSearchType,
    storedSearchInputs
  ]);

  const handleNavigation = (path: string) => {
    if (path === '/dashboard') {
      localStorage.removeItem("stockSearchTerm");
      localStorage.removeItem("stockSearchType");
      localStorage.removeItem("stockSearchInputs");
      localStorage.removeItem("stockOutputSearchQuery");
      localStorage.removeItem("stockOutputSearchType");
      localStorage.removeItem("stockOutputSearchInputs");
      resetSearch(defaultGetInputsRequestParams);
    }
    else {
      const currentSearchTerm = getValuesSearch("searchTerm");
      const currentSearchInputs = getValuesSearch();
      setStoredSearchTerm(currentSearchTerm);
      setStoredSearchType(searchTypeValue);
      setStoredSearchInputs(currentSearchInputs);
    }
    navigate(path);
  };

  const {
    register: registerOutputProjectOrPartner,
    handleSubmit: handleSubmitOutputProjectOrPartner,
    control: controlOutputProjectOrPartner,
    getValues: getValuesOutputProjectOrPartner,
    setValue: setValueOutputProjectOrPartner,
    formState: { errors: errorsOutputProjectOrPartner },
    reset: resetOutputProjectOrPartner,
  } = useForm<IStockOutputForProjectOrPartnerCreate>();
  const [driverOutputValue, setDriverOutputValue] = useState<IOption | undefined>(undefined);
  const [vehicleValue, setVehicleValue] = useState<IOption | undefined>(undefined);
  const [partnerValue, setPartnerValue] = useState<number | undefined>(undefined);
  const [outputType, setOutputType] = useState("Project");

  const {
    register: registerOutputEmployee,
    setValue: setValueOutputEmployee,
    handleSubmit: handleSubmitOutputEmployee,
    control: controlOutputEmployee,
    getValues: getValuesOutputEmployee,
    formState: { errors: errorsOutputEmployee },
    reset: resetOutputEmployee,
  } = useForm<IStockOutputForEmployeeCreate>();

  const handleOutputCreate = () => {
    if (outputValues.repository) {
      if (outputValues.repository.shortName === "HTZ") {
        setIsOpenOutputForEmployee(true);
      } else {
        setIsOpenOutputForProjectOrPartner(true);
      }
    } else {
      toast.warn("Morate prvo izabrati količine za izdavanje!");
    }
  };

  const handleReset = () => {
    setGetInputsRequestParams(defaultGetInputsRequestParams);
    resetSearch();
  };

  return (
    <>
      <nav className="container nav-arrows">
        <div>
          <InfoTooltip tooltip="Glavni meni" element="span">
            <ToolboxButton
              icon={ToolboxButtonIcon.Back}
              toolbox={true}
              onClick={() => handleNavigation('/dashboard')}
            />
          </InfoTooltip>
          <InfoTooltip tooltip="Lager - Izlaz" element="span">
            <ToolboxButton
              icon={ToolboxButtonIcon.Forward}
              toolbox={true}
              onClick={() => handleNavigation("/stock/output")}
            />
          </InfoTooltip>
        </div>
        <div>
          <ToolboxButton
            icon={ToolboxButtonIcon.Add}
            tooltip="Novi ulaz"
            toolbox={true}
            onClick={hasWriteAccessTo(user, STOCK_MODULE_NAME) ? () => setIsOpenNewInput(true) : undefined}
          />
          <ToolboxButton
            icon={ToolboxButtonIcon.Search}
            tooltip="Traži"
            toolbox={true}
            onClick={() =>
              dispatch(setSearchInputPopover({ isOpen: true }))}
          />
          {!isSearchFormEmpty
            && (<ToolboxButton
              icon={ToolboxButtonIcon.CancelSearch}
              tiny={true}
              tooltip="Pokaži sve"
              onClick={() => handleReset()}
            />)}
          <ToolboxButton
            icon={ToolboxButtonIcon.Print}
            tooltip="Štampa"
            toolbox={true}
            onClick={handlePrint}
          />
        </div>
        <div className="exemption-icon-container">
          <ToolboxButton
            icon={ToolboxButtonIcon.RemoveCircle}
            tooltip="Izdavanje"
            toolbox={true}
            onClick={hasWriteAccessTo(user, STOCK_MODULE_NAME) ? handleOutputCreate : undefined}
          />
        </div>
        <div ref={componentRef} className="print-area">
          <PrintStock />
        </div>
      </nav>
      <Popover
        isOpen={isOpenNewInput}
        onClose={() => setIsOpenNewInput(false)}
        title="Novi"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 250 }}
      >
        <NewStockInputPopover
          setIsOpen={setIsOpenNewInput}
          handleSubmit={handleSubmit}
          setValue={setValue}
          getValues={getValues}
          register={register}
          control={control}
          errors={errors}
          reset={reset}
          knownItemCodes={knownItemCodes}
          setKnownItemCodes={setKnownItemCodes}
          supplierValue={supplierValue}
          setSupplierValue={setSupplierValue}
          driverValue={driverValue}
          setDriverValue={setDriverValue}
        />
      </Popover>
      <Popover
        isOpen={searchInputPopover.isOpen}
        onClose={() => {
          dispatch(setSearchInputPopover({ isOpen: false }));
        }}
        title="Pretraga"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 310 }}
      >
        <SearchPopover
          handleSubmit={handleSubmitSearch}
          register={registerSearch}
          control={controlSearch}
          getValues={getValuesSearch}
          reset={resetSearch}
          setFocus={setFocusSearch}
          searchTypeValue={searchTypeValue}
          setSearchTypeValue={setSearchTypeValue}
          setValue={setValueSearch}
          setGetInputsRequestParams={setGetInputsRequestParams}
        />
      </Popover>
      <Popover
        isOpen={isOpenOutputForProjectOrPartner}
        onClose={() => setIsOpenOutputForProjectOrPartner(false)}
        title="Izdavanje lagera"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 570 }}
      >
        <NewStockOutputToProjectOrPartnerPopover
          handleSubmit={handleSubmitOutputProjectOrPartner}
          register={registerOutputProjectOrPartner}
          control={controlOutputProjectOrPartner}
          getValues={getValuesOutputProjectOrPartner}
          setValue={setValueOutputProjectOrPartner}
          errors={errorsOutputProjectOrPartner}
          reset={resetOutputProjectOrPartner}
          driverValue={driverOutputValue}
          setDriverValue={setDriverOutputValue}
          vehicleValue={vehicleValue}
          setVehicleValue={setVehicleValue}
          type={outputType}
          setType={setOutputType}
          partnerValue={partnerValue}
          setPartnerValue={setPartnerValue}
          close={() => setIsOpenOutputForProjectOrPartner(false)}
        />
      </Popover>

      <Popover
        isOpen={isOpenOutputForEmployee}
        onClose={() => setIsOpenOutputForEmployee(false)}
        title="Izdavanje HTZ opreme"
        direction="right"
        arrowStyle={{ top: "42px" }}
        position={{ top: 10, left: 570 }}
      >
        <NewStockOutputToEmployeePopover
          handleSubmit={handleSubmitOutputEmployee}
          setValue={setValueOutputEmployee}
          register={registerOutputEmployee}
          control={controlOutputEmployee}
          getValues={getValuesOutputEmployee}
          errors={errorsOutputEmployee}
          reset={resetOutputEmployee}
        />
      </Popover>
    </>
  );
};

export default Toolbox;
